import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons';
import { Select, Slider } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { AerisController } from '../../map_types';
import sliderStyles from '../water_temp_slider_modal/slider_modal.less';
import styles from './forecast_modal.less';

interface ForecastModalProps {
  aerisController: AerisController | null;
  open: boolean;
}

type ForecastHoursOptions = 3 | 6 | 12 | 24 | 72 | 168;

export const ForecastModal = (props: ForecastModalProps) => {
  const { aerisController, open } = props;

  const now = new Date();
  const [progressVal, setProgressVal] = useState(0);
  const [progressDate, setProgressDate] = useState<Date>(now);
  const [forecastHoursLength, setForecastHoursLength] = useState<ForecastHoursOptions>(24);
  const [isPlaying, setIsPlaying] = useState(false);

  const timelineTickCount =
    forecastHoursLength === 3 || forecastHoursLength === 72
      ? 3
      : forecastHoursLength === 168
      ? 7
      : 6;

  const progressTickCount = forecastHoursLength === 168 ? 8 : 13;

  useEffect(() => {
    if (aerisController) {
      aerisController.timeline.on('advance', (data) => {
        setProgressVal(data.position);
        setProgressDate(data.date);
      });
    }
    return () => {
      if (aerisController) {
        aerisController.timeline.off('advance');
      }
    };
  }, [aerisController]);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.modal}>
      <div className={styles.top}>
        {isPlaying ? (
          <button
            className={styles.button}
            onClick={() => {
              aerisController.timeline.pause();
              setIsPlaying(false);
            }}
          >
            <PauseOutlined />
          </button>
        ) : (
          <button
            className={styles.button}
            onClick={() => {
              setIsPlaying(true);
              if (aerisController.timeline.isActive) {
                aerisController.timeline.resume();
              } else {
                aerisController.timeline.play();
              }
            }}
          >
            <CaretRightOutlined />
          </button>
        )}
        <div className={styles.timeframeSelector}>
          <Select
            className={styles.select}
            value={forecastHoursLength}
            options={[
              { label: '3-hour Forecast', value: 3 },
              { label: '6-hour Forecast', value: 6 },
              { label: '12-hour Forecast', value: 12 },
              { label: '24-hour Forecast', value: 24 },
              { label: '3-day Forecast', value: 72 },
              { label: '7-day Forecast', value: 168 },
            ]}
            onChange={(value: ForecastHoursOptions) => {
              aerisController.timeline.pause();
              aerisController.timeline.goTo(0);
              aerisController.timeline.setEndDateUsingRelativeTime(
                `${value}hours`,
                aerisController.timeline.startDate
              );
              aerisController.timeline.duration = Math.max(5, Math.sqrt(value));
              aerisController.timeline.play();
              aerisController.timeline.pause();
              setIsPlaying(false);
              setForecastHoursLength(value);
            }}
          />
        </div>
        <div className={styles.time}>{dayjs(progressDate).format('h:mm A, ddd, MMM D')}</div>
      </div>

      <div>
        <Slider
          className={sliderStyles.slider}
          value={progressVal}
          min={0}
          max={1}
          step={0.001}
          onChange={(val: number) => {
            aerisController.timeline.pause();
            aerisController.timeline.goTo(val);
            aerisController.timeline.play();
            aerisController.timeline.pause();
            setIsPlaying(false);
          }}
          trackStyle={{
            backgroundColor: '#3F3F46',
            height: 8,
            borderRadius: 36,
          }}
          railStyle={{
            backgroundColor: '#A1A1AA80',
            height: 8,
            borderRadius: 36,
          }}
          handleStyle={{
            borderColor: '#3F3F46',
            borderWidth: 6,
            marginTop: -4,
            width: 16,
            height: 16,
          }}
          tooltip={{
            formatter: null,
          }}
        />
        <div className={styles.progressTicks}>
          {Array(progressTickCount)
            .fill(null)
            .map((_, index) => (
              <span className={styles.progressTick} key={index} />
            ))}
        </div>
        <div className={classNames(styles.progressTimes)} data-ticks={timelineTickCount + 1}>
          <span>Now</span>
          {Array(timelineTickCount)
            .fill(null)
            .map((_, index) => {
              const hoursPerTick = forecastHoursLength / timelineTickCount;
              const style =
                index === timelineTickCount - 1
                  ? { right: 0 }
                  : { left: `${(100 / timelineTickCount) * (index + 1)}%` };
              const newTime = dayjs(now).add(hoursPerTick * (index + 1), 'hour');
              const showDate = forecastHoursLength === 72 || forecastHoursLength === 168;
              return (
                <span style={style} key={index}>
                  {showDate ? (
                    <>{newTime.format('ddd')}</>
                  ) : (
                    <>
                      {newTime.format('h')}
                      <small>{newTime.format('A')}</small>
                    </>
                  )}
                </span>
              );
            })}
        </div>
      </div>
    </div>
  );
};
